
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast'; // Assuming existing toast usage
import {
    Search,
    Filter,
    Calendar,
    Clock,
    User,
    Shield,
    Activity,
    Smartphone,
    ChevronLeft,
    ChevronRight,
    ChevronsLeft,
    ChevronsRight
} from 'lucide-react';

const ActivityLogPage = () => {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [pagination, setPagination] = useState({});

    // Fetch Logs
    const fetchLogs = async (pageNum = 1, search = searchTerm) => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const response = await axios.get(`/api/admin/logs?page=${pageNum}&limit=10&search=${search}`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.success) {
                setLogs(response.data.data);
                setPagination(response.data.pagination);
                setPage(pageNum);
            }
        } catch (error) {
            console.error('Error fetching logs:', error);
            toast.error('Failed to load activity logs');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            fetchLogs(1, searchTerm);
        }, 500);

        return () => clearTimeout(delayDebounceFn);
    }, [searchTerm]);

    // Handle initial load separately to avoid double fetch if needed, 
    // but the debounce above covers it nicely.

    const userRoleColors = {
        'Main Admin': 'bg-purple-100 text-purple-700',
        'Admin': 'bg-blue-100 text-blue-700',
        'Editor': 'bg-green-100 text-green-700',
        'Guest': 'bg-gray-100 text-gray-700'
    };

    const actionColors = {
        'LOGIN': 'text-green-600 bg-green-50',
        'LOGOUT': 'text-gray-600 bg-gray-50',
        'CREATE': 'text-blue-600 bg-blue-50',
        'UPDATE': 'text-orange-600 bg-orange-50',
        'DELETE': 'text-red-600 bg-red-50'
    };

    return (
        <div className="p-6 max-w-7xl mx-auto space-y-6">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-slate-800">System Activity Logs</h1>
                    <p className="text-slate-500 mt-1">Track system events, user activities, and data changes</p>
                </div>

                {/* Global Search */}
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={20} />
                    <input
                        type="text"
                        placeholder="Search logs..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 w-full md:w-64"
                    />
                </div>
            </div>

            {/* Table Card */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-slate-50 border-b border-slate-200">
                                <th className="px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider w-16">S.No</th>
                                <th className="px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">User</th>
                                <th className="px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Role</th>
                                <th className="px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Action</th>
                                <th className="px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Details</th>
                                <th className="px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Timing</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {loading ? (
                                <tr>
                                    <td colSpan="6" className="px-6 py-12 text-center text-slate-500">
                                        <div className="flex justify-center items-center gap-2">
                                            <div className="animate-spin rounded-full h-5 w-5 border-2 border-slate-300 border-t-blue-600"></div>
                                            Loading logs...
                                        </div>
                                    </td>
                                </tr>
                            ) : logs.length === 0 ? (
                                <tr>
                                    <td colSpan="6" className="px-6 py-8 text-center text-slate-500">
                                        No activity logs found.
                                    </td>
                                </tr>
                            ) : (
                                logs.map((log, index) => (
                                    <tr key={log.id} className="hover:bg-slate-50 transition-colors cursor-default group">
                                        <td className="px-6 py-4 text-sm text-slate-500">
                                            {(pagination.page - 1) * 10 + index + 1}
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-3">
                                                <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 group-hover:bg-blue-50 group-hover:text-blue-600 transition-colors">
                                                    <User size={16} />
                                                </div>
                                                <span className="font-medium text-slate-700">{log.user_name || 'Unknown'}</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${userRoleColors[log.user_role] || 'bg-slate-100 text-slate-600'}`}>
                                                {log.user_role || 'N/A'}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-md text-xs font-semibold ${actionColors[log.action] || 'text-slate-600 bg-slate-100'}`}>
                                                {log.action}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className="text-sm text-slate-600 truncate max-w-xs block" title={log.details}>
                                                {log.details}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex flex-col">
                                                <span className="text-sm font-medium text-slate-700">
                                                    {new Date(log.created_at).toLocaleDateString()}
                                                </span>
                                                <span className="text-xs text-slate-500">
                                                    {new Date(log.created_at).toLocaleTimeString()}
                                                </span>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                {/* Pagination */}
                {!loading && pagination.totalPages > 1 && (
                    <div className="bg-slate-50 px-6 py-4 border-t border-slate-200 flex flex-col md:flex-row items-center justify-between gap-4">
                        <div className="text-sm text-slate-500">
                            Showing <span className="font-medium">{(pagination.page - 1) * 10 + 1}</span> to <span className="font-medium">{Math.min(pagination.page * 10, pagination.total)}</span> of <span className="font-medium">{pagination.total}</span> results
                        </div>

                        <div className="flex items-center gap-1">
                            {/* Previous Button */}
                            <button
                                onClick={() => fetchLogs(page - 1, searchTerm)}
                                disabled={page === 1}
                                className="p-2 rounded-md border border-slate-300 bg-white text-slate-500 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                title="Previous Page"
                            >
                                <ChevronLeft size={16} />
                            </button>

                            {/* Page Numbers */}
                            {(() => {
                                const totalPages = pagination.totalPages;
                                const currentPage = pagination.page;
                                const maxPagesToShow = 5;
                                let startPage = Math.max(1, currentPage - 2);
                                let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

                                if (endPage - startPage < maxPagesToShow - 1) {
                                    startPage = Math.max(1, endPage - maxPagesToShow + 1);
                                }

                                const pages = [];
                                for (let i = startPage; i <= endPage; i++) {
                                    pages.push(i);
                                }

                                return (
                                    <>
                                        {startPage > 1 && (
                                            <>
                                                <button
                                                    onClick={() => fetchLogs(1, searchTerm)}
                                                    className={`w-8 h-8 flex items-center justify-center rounded-md border text-sm font-medium transition-colors ${currentPage === 1
                                                            ? 'bg-blue-600 text-white border-blue-600'
                                                            : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'
                                                        }`}
                                                >
                                                    1
                                                </button>
                                                {startPage > 2 && <span className="px-1 text-slate-400">...</span>}
                                            </>
                                        )}

                                        {pages.map(p => (
                                            <button
                                                key={p}
                                                onClick={() => fetchLogs(p, searchTerm)}
                                                className={`w-8 h-8 flex items-center justify-center rounded-md border text-sm font-medium transition-colors ${p === currentPage
                                                        ? 'bg-blue-600 text-white border-blue-600'
                                                        : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'
                                                    }`}
                                            >
                                                {p}
                                            </button>
                                        ))}

                                        {endPage < totalPages && (
                                            <>
                                                {endPage < totalPages - 1 && <span className="px-1 text-slate-400">...</span>}
                                                <button
                                                    onClick={() => fetchLogs(totalPages, searchTerm)}
                                                    className={`w-8 h-8 flex items-center justify-center rounded-md border text-sm font-medium transition-colors ${currentPage === totalPages
                                                            ? 'bg-blue-600 text-white border-blue-600'
                                                            : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'
                                                        }`}
                                                >
                                                    {totalPages}
                                                </button>
                                            </>
                                        )}
                                    </>
                                );
                            })()}

                            {/* Next Button */}
                            <button
                                onClick={() => fetchLogs(page + 1, searchTerm)}
                                disabled={page === pagination.totalPages}
                                className="p-2 rounded-md border border-slate-300 bg-white text-slate-500 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                title="Next Page"
                            >
                                <ChevronRight size={16} />
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ActivityLogPage;
